import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  loginUser,
  registerUser,
  submitContactForm, 
  
} from "../api/authApi";
import axios from "axios";
import { BASE_URL } from "../config/config";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState({});
  const [userName, setUserName] = useState("");

  const sessionTimeoutRef = useRef(null);

  // -------------------
  // Session Timeout
  // -------------------
  const clearSessionTimeout = useCallback(() => {
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }
  }, []);

  const handleLogout = useCallback(
    (isSessionExpired = false) => {
      clearSessionTimeout();
      setUser(null);
      setToken(null);
      setUserName("");
      sessionStorage.removeItem("user");
      sessionStorage.removeItem("token");

      if (isSessionExpired) {
        console.warn("Session expired. Please log in again.");
      }
      navigate("/login");
    },
    [navigate, clearSessionTimeout]
  );

  const startSessionTimeout = useCallback(() => {
    clearSessionTimeout();
    sessionTimeoutRef.current = setTimeout(() => {
      handleLogout(true); // Session expired
    }, 15 * 60 * 1000); // 15 minutes
  }, [handleLogout, clearSessionTimeout]);

  // -------------------
  // Fetch User Details
  // -------------------
  const getUserDetails = useCallback(
    async (authToken = token) => {
      try {
        const response = await axios.get(`${BASE_URL}/user`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        const userData = response.data?.data;
        if (userData) {
          setUser(userData);
          setUserName(userData.firstName || "");
          sessionStorage.setItem("user", JSON.stringify(userData));
        }
      } catch (error) {
        console.error("Failed to fetch user details", error);
        if (error.response?.status === 401) {
          handleLogout(true); // Token expired or invalid
        }
      } finally {
        setLoading(false);
      }
    },
    [token, handleLogout]
  );

  // -------------------
  // Register User
  // -------------------
  const handleRegister = async (formData) => {
    try {
      await registerUser(formData);
      return { success: true };
    } catch (err) {
      const message = err?.response?.data?.message || "Signup failed";
      return { success: false, message };
    }
  };

  // -------------------
  // Login User
  // -------------------
  const handleLogin = async (credentials) => {
  try {
    const res = await loginUser(credentials);
    const { token, data } = res.data;

    if (!token || !data) {
      throw new Error("Token or user missing from API response");
    }

    // ✅ SET USER IMMEDIATELY
    setToken(token);
    setUser(data);
    setUserName(data.firstName || "");

    sessionStorage.setItem("token", token);
    sessionStorage.setItem("user", JSON.stringify(data));

    startSessionTimeout();

    return { success: true };
  } catch (err) {
    const message =
      err?.response?.data?.message || err?.message || "Login failed";
    return { success: false, message };
  }
};


  // -------------------
  // Submit Contact Form ✅
  // -------------------
  const handleContactFormSubmit = async (formData) => {
    try {
      const res = await submitContactForm(formData);
      return { success: true, message: res?.message || "Submitted successfully!" };
    } catch (err) {
      return {
        success: false,
        message: err?.message || "Failed to send message",
      };
    }
  };

  // -------------------
  // Load Auth State from Session
  // -------------------
  useEffect(() => {
    const storedToken = sessionStorage.getItem("token");
    const storedUser = sessionStorage.getItem("user");

    if (storedToken) {
      setToken(storedToken);
      if (storedUser && storedUser !== "undefined") {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setUserName(parsedUser?.firstName || "");
        setLoading(false);
      } else {
        getUserDetails(storedToken);
      }
      startSessionTimeout();
    } else {
      setLoading(false);
    }
  }, [getUserDetails, startSessionTimeout]);

  // -------------------
  // Fetch Company Info
  // -------------------
  useEffect(() => {
    axios
      .get(`${BASE_URL}/company`)
      .then((res) => {
        if (Array.isArray(res.data?.data) && res.data.data.length > 0) {
          setCompany(res.data.data[0]);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        userName,
        isLoggedIn: !!user && !!token,
        loading,
        company,
        handleLogin,
        handleRegister,
        handleLogout,
        handleContactFormSubmit, // ✅ exposed
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);




